import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { ExternalLink, TrendingUp } from 'lucide-react'

const Portfolio = () => {
  const [filter, setFilter] = useState('all')

  const projects = [
    {
      title: 'Global Manufacturing ERP',
      category: 'erp',
      industry: 'Manufacturing',
      description: 'Complete Odoo ERP implementation for multi-location manufacturing operations',
      result: '40% efficiency increase',
      image: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['Odoo', 'ERP', 'Manufacturing']
    },
    {
      title: 'FinTech SaaS Platform',
      category: 'saas',
      industry: 'Financial Services',
      description: 'Multi-tenant payment processing platform with advanced analytics',
      result: '10,000+ active users',
      image: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['SaaS', 'FinTech', 'React']
    },
    {
      title: 'E-Commerce Integration',
      category: 'web',
      industry: 'Retail',
      description: 'Custom Odoo e-commerce with inventory management and CRM',
      result: '3x revenue growth',
      image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['Odoo', 'E-Commerce', 'Integration']
    },
    {
      title: 'Healthcare Management System',
      category: 'custom',
      industry: 'Healthcare',
      description: 'HIPAA-compliant patient management and scheduling system',
      result: '50% admin time saved',
      image: 'https://images.pexels.com/photos/3825517/pexels-photo-3825517.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['Custom Software', 'Healthcare', 'Security']
    },
    {
      title: 'AI-Powered Analytics Dashboard',
      category: 'ai',
      industry: 'Technology',
      description: 'Real-time business intelligence with predictive analytics',
      result: '60% faster insights',
      image: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['AI', 'Analytics', 'Dashboard']
    },
    {
      title: 'Supply Chain Automation',
      category: 'erp',
      industry: 'Logistics',
      description: 'End-to-end supply chain management with Odoo',
      result: '35% cost reduction',
      image: 'https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=800',
      tags: ['Odoo', 'Automation', 'Logistics']
    },
  ]

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'erp', label: 'ERP Solutions' },
    { id: 'saas', label: 'SaaS Platforms' },
    { id: 'web', label: 'Web Development' },
    { id: 'custom', label: 'Custom Software' },
    { id: 'ai', label: 'AI & Automation' },
  ]

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter)

  return (
    <div className="min-h-screen pt-20" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="1:1" data-component-id="client/src/pages/Portfolio.jsx:1:1">
      <section 
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-component-path="client/src/pages/Portfolio.jsx"
        data-component-line="2:3"
        data-component-id="client/src/pages/Portfolio.jsx:2:3"
      >
        <div className="absolute inset-0 bg-dark-primary/80" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="10:5" data-component-id="client/src/pages/Portfolio.jsx:10:5"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="12:5" data-component-id="client/src/pages/Portfolio.jsx:12:5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-component-path="client/src/pages/Portfolio.jsx"
            data-component-line="13:7"
            data-component-id="client/src/pages/Portfolio.jsx:13:7"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="18:9" data-component-id="client/src/pages/Portfolio.jsx:18:9">
              Our <span className="text-gradient" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="19:19" data-component-id="client/src/pages/Portfolio.jsx:19:19">Portfolio</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="21:9" data-component-id="client/src/pages/Portfolio.jsx:21:9">
              Real success stories from businesses we've transformed
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-dark-primary" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="28:3" data-component-id="client/src/pages/Portfolio.jsx:28:3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="29:5" data-component-id="client/src/pages/Portfolio.jsx:29:5">
          <div className="flex flex-wrap justify-center gap-4 mb-12" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="30:7" data-component-id="client/src/pages/Portfolio.jsx:30:7">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFilter(cat.id)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  filter === cat.id
                    ? 'bg-neon-green text-dark-primary'
                    : 'glass-panel text-gray-400 hover:text-white hover:border-neon-green/50'
                }`}
                data-component-path="client/src/pages/Portfolio.jsx"
                data-component-line="32:11"
                data-component-id="client/src/pages/Portfolio.jsx:32:11"
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="46:7" data-component-id="client/src/pages/Portfolio.jsx:46:7">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="glass-panel rounded-xl overflow-hidden hover:neon-border transition-all group"
                data-component-path="client/src/pages/Portfolio.jsx"
                data-component-line="48:11"
                data-component-id="client/src/pages/Portfolio.jsx:48:11"
              >
                <div className="relative h-56 overflow-hidden" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="55:13" data-component-id="client/src/pages/Portfolio.jsx:55:13">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800'
                    }}
                    data-component-path="client/src/pages/Portfolio.jsx"
                    data-component-line="56:15"
                    data-component-id="client/src/pages/Portfolio.jsx:56:15"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-primary/90 to-transparent" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="67:15" data-component-id="client/src/pages/Portfolio.jsx:67:15"></div>
                </div>
                <div className="p-6" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="69:13" data-component-id="client/src/pages/Portfolio.jsx:69:13">
                  <div className="text-neon-green text-sm font-semibold mb-2" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="70:15" data-component-id="client/src/pages/Portfolio.jsx:70:15">{project.industry}</div>
                  <h3 className="text-2xl font-bold mb-3 text-white" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="71:15" data-component-id="client/src/pages/Portfolio.jsx:71:15">{project.title}</h3>
                  <p className="text-gray-400 mb-4" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="72:15" data-component-id="client/src/pages/Portfolio.jsx:72:15">{project.description}</p>
                  <div className="flex items-center gap-2 text-neon-green mb-4" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="73:15" data-component-id="client/src/pages/Portfolio.jsx:73:15">
                    <TrendingUp className="w-5 h-5" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="74:17" data-component-id="client/src/pages/Portfolio.jsx:74:17" />
                    <span className="font-semibold" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="75:17" data-component-id="client/src/pages/Portfolio.jsx:75:17">{project.result}</span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="77:15" data-component-id="client/src/pages/Portfolio.jsx:77:15">
                    {project.tags.map((tag, i) => (
                      <span key={i} className="px-3 py-1 bg-neon-green/10 text-neon-green text-xs rounded-full" data-component-path="client/src/pages/Portfolio.jsx" data-component-line="79:19" data-component-id="client/src/pages/Portfolio.jsx:79:19">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Portfolio